import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from './cn';
import { IconButton } from './IconButton';
import { Input } from './Input';
// Type only — the header used `Select` before both pickers became grids. The option
// shape is still the right one to describe a value/label pair.
import { type SelectOption } from './Select';

/**
 * A date field with a calendar WE own.
 *
 * ── WHY THIS EXISTS RATHER THAN `<input type="date">` ───────────────────────
 * The native control cannot be made to do two things this product needs:
 *
 *  1. SHOW A CHOSEN FORMAT. A native date input renders the order the BROWSER's
 *     locale dictates — `dd-mm-yyyy` on a UK-configured browser even on a US
 *     storefront — and no attribute, placeholder or CSS changes it.
 *  2. PLACE ITS POPUP. The browser decides where the calendar opens, and flips it
 *     above the field when the viewport is short of room below. There is no CSS
 *     hook. Sizing a hidden anchor to steer it was tried at five different widths
 *     in US-Shop and never held.
 *
 * So the calendar here is a popover this component renders and positions:
 * right-aligned to the field, under the button that opens it, flipping above only
 * when it genuinely would not fit — and identically in every browser.
 *
 * ── WHAT THIS DOES NOT DO — AND MUST NOT ───────────────────────────────────
 * ⚠️ No business rules. It does not know about minimum ages, enrollment windows,
 * billing periods or holidays. `min`/`max` are presentation bounds (grey out what
 * cannot be picked); the RULE behind a bound belongs in the consuming app's
 * `services/`, which must still validate. A caller that relies on this component
 * to enforce "18 or older" has put a domain rule in the component library.
 *
 * ── ISO IN, ISO OUT ────────────────────────────────────────────────────────
 * `value` and `onChange` are always `YYYY-MM-DD` (or `''`). Only the DISPLAY is
 * reordered, so form state, validation and wire payloads never deal with a
 * localised string. `onChange` fires with `''` while a partial date is being typed,
 * so a half-typed value can never be mistaken for a real one.
 */

/** Display order. ISO is unaffected — this is presentation only. */
export type DateFieldFormat = 'mm-dd-yyyy' | 'dd-mm-yyyy';

/** Every string this component renders. Nothing is defaulted to English. */
export interface DatePickerLabels {
  /** Accessible name for the trigger, e.g. "Open the calendar". */
  readonly calendar: string;
  readonly previousMonth: string;
  readonly nextMonth: string;
  /** Accessible name for the month dropdown. */
  readonly month: string;
  /** Accessible name for the year dropdown. */
  readonly year: string;
}

export interface DatePickerProps {
  readonly id?: string;
  readonly name?: string;
  /** `YYYY-MM-DD`, or `''` when unset/partial. */
  readonly value: string;
  /** Fires with `YYYY-MM-DD`, or `''` while the typed value is incomplete/impossible. */
  readonly onChange: (iso: string) => void;
  /** Display order. Defaults to `mm-dd-yyyy`; `en-GB`-style apps pass `dd-mm-yyyy`. */
  readonly format?: DateFieldFormat;
  /** Earliest selectable date, `YYYY-MM-DD`. Presentation bound only — see above. */
  readonly min?: string;
  /** Latest selectable date, `YYYY-MM-DD`. Presentation bound only — see above. */
  readonly max?: string;
  readonly disabled?: boolean;
  readonly hasError?: boolean;
  readonly autoComplete?: string;
  /**
   * ⚠️ ALL USER-FACING TEXT IS INJECTED. This package ships no English: a hardcoded
   * label here would appear untranslated in all six country apps. Callers pass
   * `t()` output. Grouped rather than five flat props so adding a control does not
   * keep widening the signature.
   */
  readonly labels: DatePickerLabels;
  /** Placeholder for the text field. Defaults to the format token itself. */
  readonly placeholder?: string;
  /** BCP-47 tag driving month and weekday names via `Intl`. */
  readonly locale?: string;
  readonly className?: string;
}

// ── Pure helpers. Exported because they are the seam a consumer's own tests can
// ── pin, and because this package has no test harness of its own yet.

/** Years shown per page in the year view — a 3×4 grid, so the panel never grows. */
const YEARS_PER_PAGE = 12;

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD` → the display order, or `''` when not a full ISO date. */
export function isoToDateDisplay(iso: string, format: DateFieldFormat): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso);
  if (match === null) return '';
  const [, year, month, day] = match;
  return format === 'mm-dd-yyyy' ? `${month}-${day}-${year}` : `${day}-${month}-${year}`;
}

/**
 * Progressive input mask. Separators appear as the field fills and vanish again on
 * backspace, so deleting never traps the caret behind a dash.
 */
export function maskDateInput(raw: string): string {
  const digits = raw.replace(/\D/gu, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
}

/**
 * Display order → `YYYY-MM-DD`, or `''` when incomplete OR NOT A REAL DATE.
 *
 * ⚠️ The real-date check is the point. A native input could only ever emit a date
 * that exists; free text can produce `13-45-1990`. Passing that through as
 * `1990-13-45` satisfies a naive `\d{4}-\d{2}-\d{2}` check downstream and then
 * fails much later as an invalid `Date`, reported as whatever that consumer's
 * fallback message happens to be. Refusing it here keeps the failure honest.
 */
export function dateDisplayToIso(display: string, format: DateFieldFormat): string {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/u.exec(display);
  if (match === null) return '';
  const [, first, second, year] = match;
  const month = format === 'mm-dd-yyyy' ? first : second;
  const day = format === 'mm-dd-yyyy' ? second : first;
  const iso = `${year}-${month}-${day}`;
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return '';
  // Round-trip: a non-existent date (2026-02-30) silently rolls over, so comparing
  // back is what actually proves it exists.
  return parsed.toISOString().slice(0, 10) === iso ? iso : '';
}

/** Six weeks of ISO dates covering `month`, Sunday-first, including bleed days. */
export function monthGrid(year: number, month: number): readonly (readonly string[])[] {
  const firstOfMonth = new Date(Date.UTC(year, month, 1));
  const start = new Date(Date.UTC(year, month, 1 - firstOfMonth.getUTCDay()));
  const weeks: string[][] = [];
  for (let week = 0; week < 6; week += 1) {
    const row: string[] = [];
    for (let day = 0; day < 7; day += 1) {
      const cursor = new Date(start.getTime());
      cursor.setUTCDate(start.getUTCDate() + week * 7 + day);
      row.push(cursor.toISOString().slice(0, 10));
    }
    weeks.push(row);
  }
  return weeks;
}

/** ISO string comparison is chronological, so bounds need no Date parsing. */
function isOutOfRange(iso: string, min: string | undefined, max: string | undefined): boolean {
  if (min !== undefined && min !== '' && iso < min) return true;
  return max !== undefined && max !== '' && iso > max;
}

function addMonths(iso: string, delta: number): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(iso);
  if (parts === null) return iso;
  const [, year, month] = parts;
  const shifted = new Date(Date.UTC(Number(year), Number(month) - 1 + delta, 1));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-01`;
}

function addDays(iso: string, delta: number): string {
  const cursor = new Date(`${iso}T00:00:00Z`);
  cursor.setUTCDate(cursor.getUTCDate() + delta);
  return cursor.toISOString().slice(0, 10);
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

export function DatePicker({
  id,
  name,
  value,
  onChange,
  format = 'mm-dd-yyyy',
  min,
  max,
  disabled = false,
  hasError = false,
  autoComplete = 'off',
  labels,
  placeholder,
  locale = 'en-US',
  className,
}: DatePickerProps): JSX.Element {
  const [display, setDisplay] = useState(() => isoToDateDisplay(value, format));
  const [open, setOpen] = useState(false);
  /** Which day the calendar's roving tabindex is on. Also drives the visible month. */
  const [focusedIso, setFocusedIso] = useState(() => (value === '' ? todayIso() : value));
  /**
   * `days` is the month grid; `months` and `years` are the 12-cell pickers behind the
   * header buttons. Both exist so the panel's height never depends on how long the
   * selectable range is.
   */
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridId = useId();

  // A value arriving from outside (a rehydrate, a form reset) must reach the field.
  // Guarded on the ISO round-trip so it cannot clobber a half-typed value.
  useEffect(() => {
    if (dateDisplayToIso(display, format) === value) return;
    setDisplay(isoToDateDisplay(value, format));
  }, [value, format, display]);

  const closeAndReturnFocus = useCallback(() => {
    setOpen(false);
  }, []);

  // Click-outside and Escape. Registered only while open, so a page with many of
  // these fields carries no idle listeners.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent): void => {
      if (wrapperRef.current?.contains(event.target as Node) === true) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeAndReturnFocus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, closeAndReturnFocus]);

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }),
    [locale],
  );
  const weekdayNames = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    // 2024-01-07 is a Sunday, matching `monthGrid`'s Sunday-first rows.
    return Array.from({ length: 7 }, (_, index) =>
      formatter.format(new Date(Date.UTC(2024, 0, 7 + index))),
    );
  }, [locale]);

  const visible = useMemo(() => {
    const parts = /^(\d{4})-(\d{2})/u.exec(focusedIso);
    if (parts === null) return { year: 1970, month: 0 };
    return { year: Number(parts[1]), month: Number(parts[2]) - 1 };
  }, [focusedIso]);

  const weeks = useMemo(() => monthGrid(visible.year, visible.month), [visible]);

  const monthOptions = useMemo<SelectOption[]>(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: String(index),
        label: monthFormatter.format(new Date(Date.UTC(2024, index, 1))),
      })),
    [monthFormatter],
  );

  /**
   * The selectable year window, derived from `min`/`max` rather than fixed: a date of
   * birth reaches back a century, a service-start date a year or two forward.
   */
  const yearBounds = useMemo(() => {
    const currentYear = Number(todayIso().slice(0, 4));
    const first = min !== undefined && min !== '' ? Number(min.slice(0, 4)) : currentYear - 120;
    const last = max !== undefined && max !== '' ? Number(max.slice(0, 4)) : currentYear + 10;
    return { first: Math.min(first, last), last: Math.max(first, last) };
  }, [min, max]);

  /**
   * The year PAGE currently shown in the year view — a 12-year block.
   *
   * ⚠️ A DROPDOWN WAS THE WRONG CONTROL HERE. A birth-year list spans ~120 entries,
   * which a native `<select>` renders as a popup taller than the page — it covered the
   * consent checkboxes and the submit button. A paged grid is bounded at twelve cells
   * however wide the range is, and it is what platform date pickers do.
   */
  const [yearPageStart, setYearPageStart] = useState(() => {
    const year = Number(focusedIso.slice(0, 4));
    return year - ((year - yearBounds.first) % YEARS_PER_PAGE);
  });

  /** Jump the visible month, clamping the day so 31 → February does not roll over. */
  const jumpTo = (year: number, month: number): void => {
    const lastDayOfTarget = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const day = Math.min(Number(focusedIso.slice(8, 10)), lastDayOfTarget);
    setFocusedIso(`${year}-${pad2(month + 1)}-${pad2(day)}`);
  };

  const commit = (iso: string): void => {
    setDisplay(isoToDateDisplay(iso, format));
    onChange(iso);
    setFocusedIso(iso);
    setOpen(false);
  };

  const handleType = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const masked = maskDateInput(event.target.value);
    setDisplay(masked);
    const iso = dateDisplayToIso(masked, format);
    onChange(iso);
    if (iso !== '') setFocusedIso(iso);
  };

  const handleGridKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const moves: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    const delta = moves[event.key];
    if (delta !== undefined) {
      event.preventDefault();
      setFocusedIso((current) => addDays(current, delta));
      return;
    }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      setFocusedIso((current) => addMonths(current, event.key === 'PageUp' ? -1 : 1));
    }
  };


  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <Input
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete={autoComplete}
        disabled={disabled}
        hasError={hasError}
        maxLength={10}
        placeholder={placeholder ?? format}
        value={display}
        onChange={handleType}
        // Room for the trigger so a full date never runs underneath it.
        className="pr-12"
      />
      {/*
        ⚠️ The positioning lives on this SPAN, not on the IconButton. `cn()` here is
        plain `clsx` — not tailwind-merge — so a `className` does NOT override a base
        class in the same utility group: both are emitted and CSS order decides.
        `IconButton`'s base begins with `relative`, and Tailwind emits `.relative`
        after `.absolute`, so passing `absolute` to the button silently loses and it
        drops into normal flow. A positioned wrapper cannot regress that way.
      */}
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2">
        <IconButton
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          ariaLabel={labels.calendar}
          title={labels.calendar}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => {
            setFocusedIso(value === '' ? todayIso() : value);
            setOpen((wasOpen) => !wasOpen);
          }}
          className="text-foreground/60 hover:bg-primary/5 hover:text-foreground"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M8 2v4M16 2v4M3 10h18" />
            </svg>
          }
        />
      </span>

      {open ? (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={labels.calendar}
          // RIGHT-ALIGNED to the field and ALWAYS BELOW IT. Opening below only is a
          // deliberate constraint: a panel that sometimes flips above moves the day
          // cells out from under the cursor between one opening and the next, and a
          // date field this far up a form always has room. If it ever needs to sit in
          // the last 340px of a page, scroll the field into view instead of flipping.
          className={cn(
            'absolute right-0 top-full z-50 mt-1 w-[17.5rem] rounded-md border border-border',
            'bg-surface p-3 shadow-float',
          )}
        >
          <div className="mb-2 flex items-center gap-1">
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              ariaLabel={labels.previousMonth}
              title={labels.previousMonth}
              onClick={() => {
                // The arrows page whatever is on screen: a year block, a year, or a
                // month. Leaving them on months while a year grid is shown would move
                // something the user cannot see.
                if (view === 'years') setYearPageStart((start) => start - YEARS_PER_PAGE);
                else if (view === 'months') jumpTo(visible.year - 1, visible.month);
                else setFocusedIso((current) => addMonths(current, -1));
              }}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              }
            />
            {/*
              Month and year both open a PAGED GRID rather than a dropdown, and they
              are deliberately the same control so the panel behaves one way. A native
              `<select>` was tried for both: the year's ~120 entries rendered a popup
              taller than the page, and keeping month as a dropdown while year was a
              grid left two different interactions side by side.
            */}
            <button
              type="button"
              aria-label={labels.month}
              aria-expanded={view === 'months'}
              onClick={() => setView((current) => (current === 'months' ? 'days' : 'months'))}
              className={cn(
                'h-8 flex-1 rounded-md border px-2 text-xs font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                view === 'months'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-bg text-foreground hover:bg-primary/5',
              )}
            >
              {monthOptions[visible.month]?.label ?? ''}
            </button>
            {/*
              Opens the paged year grid rather than a ~120-entry dropdown. `Button`
              would impose its own sizing here, so this is a plain button styled to
              match the adjacent `Select` — the only bespoke control in the panel, and
              it exists because no primitive covers "toggle a sub-view".
            */}
            <button
              type="button"
              aria-label={labels.year}
              aria-expanded={view === 'years'}
              onClick={() => {
                setYearPageStart(
                  visible.year - ((visible.year - yearBounds.first) % YEARS_PER_PAGE),
                );
                setView((current) => (current === 'years' ? 'days' : 'years'));
              }}
              className={cn(
                'h-8 rounded-md border px-2 text-xs font-medium transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                view === 'years'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border bg-bg text-foreground hover:bg-primary/5',
              )}
            >
              {visible.year}
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              ariaLabel={labels.nextMonth}
              title={labels.nextMonth}
              onClick={() => {
                if (view === 'years') setYearPageStart((start) => start + YEARS_PER_PAGE);
                else if (view === 'months') jumpTo(visible.year + 1, visible.month);
                else setFocusedIso((current) => addMonths(current, 1));
              }}
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              }
            />
          </div>

          {view === 'months' ? (
            /* Twelve months, 3×4 — same footprint as the year page. */
            <div role="grid" aria-label={labels.month} className="grid grid-cols-3 gap-1">
              {monthOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  role="gridcell"
                  data-month={index}
                  aria-selected={index === visible.month}
                  onClick={() => {
                    jumpTo(visible.year, index);
                    setView('days');
                  }}
                  className={cn(
                    'h-9 rounded-md px-1 text-xs transition-colors',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    index === visible.month
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-primary/10',
                  )}
                >
                  {/* Abbreviated so a long localised name cannot widen the panel. */}
                  {option.label.slice(0, 3)}
                </button>
              ))}
            </div>
          ) : view === 'years' ? (
            /*
              The year page. Twelve cells, so the panel's height is constant no matter
              how long the selectable range is — the point of replacing the dropdown.
              Paging is disabled at the ends rather than hidden, so the control does not
              change shape as you reach the boundary.
            */
            <div role="grid" aria-label={labels.year} className="grid grid-cols-3 gap-1">
              {Array.from({ length: YEARS_PER_PAGE }, (_, index) => {
                const year = yearPageStart + index;
                const outOfRange = year < yearBounds.first || year > yearBounds.last;
                return (
                  <button
                    key={year}
                    type="button"
                    role="gridcell"
                    data-year={year}
                    disabled={outOfRange}
                    aria-selected={year === visible.year}
                    onClick={() => {
                      jumpTo(year, visible.month);
                      setView('days');
                    }}
                    className={cn(
                      'h-9 rounded-md text-sm transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      outOfRange && 'cursor-not-allowed opacity-30',
                      !outOfRange && year !== visible.year && 'text-foreground hover:bg-primary/10',
                      year === visible.year && 'bg-primary text-primary-foreground',
                    )}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          ) : (
          /* `role="grid"` + one roving tabindex: arrow keys move, Enter/Space picks. */
          <div
            role="grid"
            aria-labelledby={gridId}
            onKeyDown={handleGridKeyDown}
            className="grid grid-cols-7 gap-0.5"
          >
            {weekdayNames.map((weekday) => (
              <span
                key={weekday}
                role="columnheader"
                aria-label={weekday}
                className="pb-1 text-center text-[11px] font-medium text-foreground/50"
              >
                {weekday.slice(0, 2)}
              </span>
            ))}
            {weeks.map((week) =>
              week.map((iso) => {
                const inMonth = Number(iso.slice(5, 7)) === visible.month + 1;
                const disabledDay = isOutOfRange(iso, min, max);
                const selected = iso === value;
                const focused = iso === focusedIso;
                return (
                  <button
                    key={iso}
                    type="button"
                    role="gridcell"
                    // The grid always shows six weeks, so bleed days from the
                    // neighbouring months are present and a day NUMBER can appear
                    // twice (Jan 29 and Feb 29 both sit in February's grid). This
                    // exposes the unambiguous date for tests and debugging.
                    data-iso={iso}
                    data-outside-month={inMonth ? undefined : 'true'}
                    aria-selected={selected}
                    aria-current={iso === todayIso() ? 'date' : undefined}
                    disabled={disabledDay}
                    tabIndex={focused ? 0 : -1}
                    ref={(node) => {
                      // Follow the roving tabindex so keyboard nav actually moves focus.
                      if (focused && open && node !== null && document.activeElement !== node) {
                        node.focus();
                      }
                    }}
                    onClick={() => commit(iso)}
                    className={cn(
                      'h-8 rounded-md text-sm transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      inMonth ? 'text-foreground' : 'text-foreground/35',
                      disabledDay && 'cursor-not-allowed opacity-40',
                      !disabledDay && !selected && 'hover:bg-primary/10',
                      selected && 'bg-primary text-primary-foreground',
                    )}
                  >
                    {Number(iso.slice(8, 10))}
                  </button>
                );
              }),
            )}
          </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
